import { HttpContext, HttpContextToken } from '@angular/common/http';

/**
 * Shape every backend endpoint wraps its payload in.
 *
 * All success responses include a localized `message` (the backend honours
 * the `Accept-Language` header we send), so it is safe to surface as-is to
 * the user without any client-side translation.
 *
 * Mirrors `ApiResponse<T>` in `src/app/shared/models/api-response.models.ts`,
 * re-declared here so the core HTTP layer doesn't depend on a feature
 * folder.
 */
export interface ApiEnvelope<T = unknown> {
  statusCode: number;
  message: string;
  data: T;
  timestamp?: string;
  pagination?: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

/**
 * Returns `true` when `body` looks like an `ApiEnvelope`.
 *
 * Intentionally tolerant: only `message` is required for the success-toast
 * path to work, and `data` may be `null` / `undefined` for endpoints that
 * intentionally don't return a payload.
 */
export function isApiEnvelope(body: unknown): body is ApiEnvelope {
  if (body === null || typeof body !== 'object') return false;
  const record = body as Record<string, unknown>;
  return typeof record['message'] === 'string';
}

/**
 * Pull the localized server message out of an envelope-shaped body.
 *
 * Returns `null` for non-envelope bodies, empty strings, or whitespace-only
 * strings so callers can `if (message) toast.success(message)` safely.
 */
export function extractEnvelopeMessage(body: unknown): string | null {
  if (!isApiEnvelope(body)) return null;
  const trimmed = body.message.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/**
 * Pull `data` out of an envelope-shaped body. Useful when a caller wants to
 * normalize service code without changing every endpoint at once.
 */
export function extractEnvelopeData<T = unknown>(body: unknown): T | null {
  if (!isApiEnvelope(body)) return null;
  return (body.data ?? null) as T | null;
}

/**
 * Localized fallback used when an error body doesn't include a message we
 * can show. The error interceptor sources the actual string via
 * `TranslateService` against this key.
 */
export const REQUEST_FAILED_FALLBACK_KEY = 'common.errors.requestFailed';

/** A server sentence fit to show: a non-blank string, trimmed. */
function readableText(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

/**
 * The message to show an operator for a failed request. Accepts whatever a
 * `catchError` or `error:` callback receives.
 *
 * The server's own sentence wins, in any of the shapes the backend returns:
 *
 * - `{ message: "..." }` — the standard envelope
 * - `{ message: { message: "...", ... } }` — older nested wrapper
 * - `{ message: ["...", "..."] }` — class-validator-style arrays (first one)
 *
 * Otherwise the caller's localized `fallback`. Never `error.message` or
 * `statusText`: those are Angular's English diagnostics ("Http failure
 * response for <url>: 0 Unknown Error"), set on every failure, and are for
 * logs, not operators (F-016). A blank server message counts as none (F-017).
 */
export function extractErrorMessage(error: unknown, fallback: string): string {
  const body = (error as { error?: unknown } | null | undefined)?.error;
  const message =
    body !== null && typeof body === 'object'
      ? (body as { message?: unknown }).message
      : undefined;
  if (Array.isArray(message)) {
    return message.map(readableText).find(Boolean) ?? fallback;
  }
  if (message !== null && typeof message === 'object') {
    return readableText((message as { message?: unknown }).message) ?? fallback;
  }
  return readableText(message) ?? fallback;
}

/**
 * Per-request opt-out: when set to `true`, the success-toast interceptor
 * will NOT call `toastService.success(...)` even if the response body
 * carries a server-localized message.
 *
 * Use this for "silent" endpoints (background polling, refresh-token,
 * etc.) or for screens that already drive their own success UX.
 */
export const SUPPRESS_SUCCESS_TOAST = new HttpContextToken<boolean>(
  () => false
);

/**
 * Per-request opt-out for the error interceptor's automatic error toast.
 *
 * Set when a screen wants to render the error inline (e.g. a form-level
 * `mat-error`) instead of via the global toast.
 */
export const SUPPRESS_ERROR_TOAST = new HttpContextToken<boolean>(() => false);

/**
 * Per-request opt-in: by default we only auto-toast success for mutations
 * (`POST` / `PUT` / `PATCH` / `DELETE`). Set this when a `GET` should also
 * surface its envelope message (rare — typically a long-running async job).
 */
export const SHOW_SUCCESS_TOAST_ON_GET = new HttpContextToken<boolean>(
  () => false
);

/** Convenience: returns an `HttpContext` with success-toasting disabled. */
export function withSuppressSuccessToast(
  context: HttpContext = new HttpContext()
): HttpContext {
  return context.set(SUPPRESS_SUCCESS_TOAST, true);
}

/** Convenience: returns an `HttpContext` with error-toasting disabled. */
export function withSuppressErrorToast(
  context: HttpContext = new HttpContext()
): HttpContext {
  return context.set(SUPPRESS_ERROR_TOAST, true);
}

/** Convenience: returns an `HttpContext` that opts a GET into success toast. */
export function withShowSuccessToastOnGet(
  context: HttpContext = new HttpContext()
): HttpContext {
  return context.set(SHOW_SUCCESS_TOAST_ON_GET, true);
}

/** Set of HTTP methods we treat as "mutations" for default toast behaviour. */
export const TOAST_DEFAULT_METHODS: ReadonlySet<string> = new Set([
  'POST',
  'PUT',
  'PATCH',
  'DELETE',
]);
